import { ChangeEvent, useEffect, useState } from 'react'
import { HStack, Checkbox, CheckboxGroup, Input, Stack, Text, Badge } from '@chakra-ui/react'

import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import useCustomFormGetDefaultValueAsArray from '../../hooks/useCustomFormGetDefaultValueAsArray'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import validateMultipleSelectionBoxOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateMultipleSelectionBoxOnCustomForm'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormMultipleSelectionBoxQuestion({
  question,
  questionKey,
  disabled,
  formMethods,
}: CustomFormViewerQuestionProps) {
  const [otherOptionEnabled, setOtherOptionEnabled] = useState<boolean>(false)
  const [otherOptionValue, setOtherOptionValue] = useState<string>(' ')

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  const { defaultValue } = useCustomFormGetDefaultValueAsArray({
    question,
    questionKey,
    formMethods,
  })

  const hasLongOption = question.options?.some((option) => option.length > 30) ?? false

  const hasManyOptions = question && question.options && question.options?.length >= 6

  const hasToBeColumns = hasLongOption || hasManyOptions || question?.options?.includes('Outro')

  const optionsValues: string[] =
    formMethods && formMethods.watch ? formMethods.watch(questionKey) || defaultValue || [] : defaultValue || []

  function handleOnChangeCheckbox(newOption: string) {
    if (optionsValues.includes(newOption)) {
      const filteredOptionsValue = optionsValues.filter((option) => option !== newOption)
      formMethods?.setValue(questionKey, filteredOptionsValue)
    } else {
      formMethods?.setValue(questionKey, [...optionsValues, newOption])
    }
  }

  function handleOnCheckCheckbox() {
    const currentValue = !otherOptionEnabled

    if (currentValue) {
      const updateOptionsValues = [...optionsValues]
      updateOptionsValues.push(otherOptionValue)
      formMethods?.setValue(questionKey, updateOptionsValues)
    } else {
      const filteredOptionsValues = optionsValues.filter((option) => option !== otherOptionValue)
      formMethods?.setValue(questionKey, filteredOptionsValues)
    }
    setOtherOptionEnabled(currentValue)
  }

  function handleOnChangeInput(event: ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value

    if (otherOptionEnabled) {
      const filteredOptionsValues = optionsValues.filter((option) => option !== otherOptionValue)
      const updateOptionsValues = [...filteredOptionsValues]
      updateOptionsValues.push(inputValue)
      formMethods?.setValue(questionKey, updateOptionsValues)
    }
    setOtherOptionValue(inputValue)
  }

  useEffect(() => {
    if (optionsValues && optionsValues.length > 0) {
      const errorMessage = validateMultipleSelectionBoxOnCustomForm({ options: optionsValues, question })
      if (errorMessage === '') {
        removeErrorFromQuestion(question.slug)
      } else {
        setErrorOnQuestion(question, errorMessage)
      }
    }
  }, [optionsValues])

  useEffect(() => {
    if (formMethods && formMethods.setValue) {
      if (question.answer) {
        formMethods.setValue(questionKey, question.answer)
      }
    }
  }, [question])

  useCustomFormHandleErrorWithScrollBehavior({
    questionWithError,
    questionSlug: question.slug,
  })

  return (
    <Stack id={question.slug}>
      <CheckboxGroup size="md" value={optionsValues}>
        <Stack direction={hasToBeColumns ? 'column' : 'row'} spacing="24px">
          {question?.options?.map((option) => {
            if (option === 'Outro') {
              return (
                <HStack key={option}>
                  <Checkbox
                    isDisabled={disabled}
                    alignItems={hasToBeColumns ? 'start' : 'center'}
                    isChecked={otherOptionEnabled}
                    onChange={() => handleOnCheckCheckbox()}
                  />

                  <Text marginTop={hasToBeColumns ? '-2px' : '0px'}>{option}</Text>

                  <Input
                    autoComplete="off"
                    isDisabled={disabled}
                    value={otherOptionValue}
                    onChange={handleOnChangeInput}
                    rounded="none"
                    bg="transparent"
                    border="none"
                    borderBottom="2px"
                    color="dark"
                  />
                </HStack>
              )
            }
            return (
              <HStack key={option}>
                <Checkbox
                  alignItems={hasToBeColumns ? 'start' : 'center'}
                  isDisabled={disabled}
                  value={option}
                  onChange={() => handleOnChangeCheckbox(option)}
                />

                <Text marginTop={hasToBeColumns ? '-2px' : '0px'}>{option}</Text>
              </HStack>
            )
          })}
        </Stack>
      </CheckboxGroup>

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />

      {question.validation && (
        <HStack mt={2} flexWrap="wrap">
          {question.validation.min && <Badge textTransform="none">Mínimo de caixas ({question.validation.min})</Badge>}
          {question.validation.max && <Badge textTransform="none">Máximo de caixas ({question.validation.max})</Badge>}
        </HStack>
      )}
    </Stack>
  )
}
